<template>
  <div class="modal fade" id="totalsModal" tabindex="-1" role="dialog" aria-labelledby="totalsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-md" role="document">
      <div class="modal-content">
        <div class="modal-header" style="display: flex; align-items: center; justify-content: space-between;">
          <h5 class="modal-title" id="totalsModalLabel" style="margin: 0;">Column Totals</h5>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close" style="margin: 0; padding: 0; border: none; background: none; font-size: 1.5rem; line-height: 1; flex-shrink: 0;">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <!-- Search Filter Alert -->
          <div v-if="activeSearchFilter" class="alert alert-warning mb-3">
            <i class="fa fa-search"></i> <strong>Search Filter Affecting Totals:</strong> "{{activeSearchFilter}}"
          </div>
          
          <div v-if="columnTotals.length === 0" class="alert alert-info">
            No numeric columns found to calculate totals.
          </div>
          <div v-else>
            <!-- Payment Type Filter -->
            <div v-if="paymentTypeValues.length > 0" class="form-group mb-3">
              <label for="paymentTypeFilter" style="font-weight: bold;">Filter by Payment Type:</label>
              <select id="paymentTypeFilter" class="form-control" v-model="selectedPaymentType" @change="filterByPaymentType">
                <option value="">All Payment Types</option>
                <option v-for="paymentType in paymentTypeValues" :key="paymentType" :value="paymentType">
                  {{ paymentType }}
                </option>
              </select>
            </div>
            
            <table class="table table-striped">
              <thead>
                <tr>
                  <th style="font-weight: bold;">Column Name</th>
                  <th style="font-weight: bold;">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="total in columnTotals" :key="total.columnName">
                  <td>{{ total.columnName }}</td>
                  <td>{{ formatNumber(total.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TotalsModal",
  props: {
    grid: {
      type: Object,
      required: false,
      default: null
    },
    batchRecords: {
      type: Array,
      required: false,
      default: () => []
    },
    defaultPaymentType: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      columnTotals: [],
      paymentTypeValues: [],
      selectedPaymentType: '',
      originalColumnTotals: []
    }
  },
  computed: {
    activeSearchFilter() {
      if (!this.grid) return null;
      return this.grid.search();
    }
  },
  methods: {
    show() {
      this.selectedPaymentType = this.defaultPaymentType || '';
      this.calculateColumnTotals();
      if (this.selectedPaymentType) {
        this.filterByPaymentType();
      }
      $('#totalsModal').modal('show');
    },
    calculateColumnTotals() {
      if (!this.grid || !this.batchRecords.length) {
        this.columnTotals = [];
        this.paymentTypeValues = [];
        return;
      }

      const totals = [];
      const columns = this.grid.settings().init().columns;
      
      // Get the filtered data from DataTable (respects search filters)
      const filteredData = this.grid.rows({ search: 'applied' }).data().toArray();

      // Find Payment Type column and extract unique values
      this.paymentTypeValues = [];
      const paymentTypeColumn = columns.find(column => 
        (column.title && column.title.toLowerCase() === 'payment type') ||
        (column.data && ['payment type', 'paymenttype', 'paymentmethod'].includes(column.data.toLowerCase()))
      );

      if (paymentTypeColumn) {
        const paymentTypeValues = new Set();
        filteredData.forEach(rowData => {
          let value;
          if (paymentTypeColumn.data) {
            value = this.getNestedValue(rowData, paymentTypeColumn.data);
          } else {
            const columnIndex = columns.indexOf(paymentTypeColumn);
            value = rowData[columnIndex];
          }
          if (value && value.toString().trim() !== '') {
            paymentTypeValues.add(value.toString().trim());
          }
        });
        this.paymentTypeValues = Array.from(paymentTypeValues).sort();
      }

      if (this.paymentTypeValues.length === 0 && this.defaultPaymentType) {
        this.paymentTypeValues = [this.defaultPaymentType];
      }

      columns.forEach((column, index) => {
        const columnName = column.title || column.data || `Column ${index + 1}`;
        
        // Skip columns that are likely to be dates
        if (this.isDateColumn(columnName)) {
          return;
        }
        
        // Only include columns that contain "Fee", "Pay", or "Amount"
        if (!this.isRelevantColumn(columnName)) {
          return;
        }
        
        const numericValues = [];

        // Extract numeric values from the filtered column data
        filteredData.forEach(rowData => {
          let value;
          if (column.data) {
            // Handle nested object properties (e.g., "user.name")
            value = this.getNestedValue(rowData, column.data);
          } else {
            // Fallback to array index
            value = rowData[index];
          }

          if (this.isNumeric(value)) {
            numericValues.push(parseFloat(value));
          }
        });

        if (numericValues.length > 0) {
          const total = numericValues.reduce((sum, val) => sum + val, 0);
          
          totals.push({
            columnName: columnName,
            total: total,
            count: numericValues.length
          });
        }
      });

      this.columnTotals = totals;
      this.originalColumnTotals = [...totals]; // Store original totals for filtering
    },
    filterByPaymentType() {
      if (!this.selectedPaymentType) {
        // If no payment type selected, show all totals
        this.columnTotals = [...this.originalColumnTotals];
        return;
      }

      // Recalculate totals based on selected payment type
      if (!this.grid || !this.batchRecords.length) {
        return;
      }

      const totals = [];
      const columns = this.grid.settings().init().columns;
      
      // Get the filtered data from DataTable (respects search filters)
      const filteredData = this.grid.rows({ search: 'applied' }).data().toArray();

      // Find Payment Type column
      const paymentTypeColumn = columns.find(column => 
        (column.title && column.title.toLowerCase() === 'payment type') ||
        (column.data && ['payment type', 'paymenttype', 'paymentmethod'].includes(column.data.toLowerCase()))
      );

      // Filter data by selected payment type
      const paymentTypeFilteredData = filteredData.filter(rowData => {
        let paymentTypeValue;
        if (paymentTypeColumn && paymentTypeColumn.data) {
          paymentTypeValue = this.getNestedValue(rowData, paymentTypeColumn.data);
        } else if (paymentTypeColumn) {
          const columnIndex = columns.indexOf(paymentTypeColumn);
          paymentTypeValue = rowData[columnIndex];
        } else {
          paymentTypeValue = rowData.paymentType || rowData.paymentMethod;
        }
        return paymentTypeValue && paymentTypeValue.toString().trim() === this.selectedPaymentType;
      });

      columns.forEach((column, index) => {
        const columnName = column.title || column.data || `Column ${index + 1}`;
        
        // Skip columns that are likely to be dates
        if (this.isDateColumn(columnName)) {
          return;
        }
        
        // Only include columns that contain "Fee", "Pay", or "Amount"
        if (!this.isRelevantColumn(columnName)) {
          return;
        }
        
        const numericValues = [];

        // Extract numeric values from the payment type filtered data
        paymentTypeFilteredData.forEach(rowData => {
          let value;
          if (column.data) {
            value = this.getNestedValue(rowData, column.data);
          } else {
            value = rowData[index];
          }

          if (this.isNumeric(value)) {
            numericValues.push(parseFloat(value));
          }
        });

        if (numericValues.length > 0) {
          const total = numericValues.reduce((sum, val) => sum + val, 0);
          
          totals.push({
            columnName: columnName,
            total: total,
            count: numericValues.length
          });
        }
      });

      this.columnTotals = totals;
    },
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null;
      }, obj);
    },
    isNumeric(value) {
      if (value === null || value === undefined || value === '') {
        return false;
      }
      const num = parseFloat(value);
      return !isNaN(num) && isFinite(num);
    },
    isDateColumn(columnName) {
      if (!columnName) return false;
      
      const dateKeywords = [
        'date', 'time', 'created', 'updated', 'modified', 'timestamp',
        'start', 'end', 'begin', 'finish', 'due', 'expiry', 'expiration',
        'birth', 'anniversary', 'effective', 'termination', 'enrollment',
        'processed', 'submitted', 'approved', 'rejected', 'completed',
        'dob', 'frequency'
      ];
      
      const lowerColumnName = columnName.toLowerCase();
      
      // Check if column name contains date-related keywords
      return dateKeywords.some(keyword => lowerColumnName.includes(keyword));
    },
    isRelevantColumn(columnName) {
      if (!columnName) return false;
      
      const relevantKeywords = ['transaction', 'payment', 'amount', 'cost', 'total'];
      
      const lowerColumnName = columnName.toLowerCase();
      
      // Check if column name contains relevant keywords
      return relevantKeywords.some(keyword => lowerColumnName.includes(keyword));
    },
    formatNumber(value) {
      if (typeof value === 'number') {
        return value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
      return value;
    }
  }
}
</script>

<style scoped></style> 